import React, { useMemo, useState, useCallback } from 'react';
import {
  Button,
  VStack,
  Text,
  Box
} from '@chakra-ui/react';
import { useUserQuery, usePatientLazyQuery } from "../graphql/types-and-hooks";
import { Loading } from './Loading';
import { useBookAppointmentStore } from '../store';
import queryString from 'query-string';
import { bookingFlowStore } from "../store/BookingFlowStore";
import CreateFamilyMemberStep from './CreateFamilyMemberStep';
import { BookingStep, servicebookingFlowStore } from '../store/ServiceBookingFlowStore';
import { useAlert } from './AlertProvider';
import { observer } from 'mobx-react-lite';
import { runInAction } from 'mobx'; // Import runInAction for async operations
import logger from '../utils/logger';
import { patientStore } from '../store/PatientStore';
import { logError } from "./logger";

// eslint-disable-next-line no-restricted-globals
const parsed = queryString.parse(location.search);

// Access environment variables
const USER_FK = parsed.cid;
const CLINIC_GROUP_CRM = parsed.gid;
const LOCATION_ID = parsed.lid;

interface FamilyMemberStepProps {
  onSelectFamilyMember: (member: any) => void;
  onOpenCreateFamilyMemberModal?: () => void;
}

const FamilyMemberStep = observer(({ onSelectFamilyMember, onOpenCreateFamilyMemberModal }: FamilyMemberStepProps) => {
  const [isCreateFamilyMemberOpen, setIsCreateFamilyMemberOpen] = useState(false);
  const {
    data: userData,
    loading: userLoading,
    error: userError,
  } = useUserQuery();

  const isServiceBooking = servicebookingFlowStore.isServiceBooking;
  
  // Store these values in local variables to avoid accessing observables outside reactive context
  const appointmentData = servicebookingFlowStore.appointmentData;
  const appointmentType = appointmentData?.appointmentType;
  const configurationDetails = servicebookingFlowStore.configurationDetails;

  const { showAlert: showPopUpAlert } = useAlert();

  // eslint-disable-next-line 
  const [patient, setData, resetData] = useBookAppointmentStore((state) => [
    state.patient,
    state.setData,
    state.resetData,
  ]);

  const [getPatient] = usePatientLazyQuery();

  const handleSelect = useCallback(async (memberId, member) => {
    // Access store values before async operation
    const newPatientRestriction = appointmentType?.new_patient === "0";
    
    bookingFlowStore.setSelectedFamilyMember(member);
    onSelectFamilyMember(member);
    
    logger.log('Selected family member:', member);
    try {
      const { data: patientData } = await getPatient({
        variables: {
          input: {
            clinic_group_crm: CLINIC_GROUP_CRM,
            user_fk: USER_FK,
            location_id: LOCATION_ID,
            dob: member.dob,
            first_name: member.first_name,
            last_name: member.last_name,
            contact_number: member.phone_number,
          },
        },
      });

      const familyMemberData = {
        id: patientData?.patient?.patient_id,
        type: 'familyMember',
        family_member_key: memberId,
        first_name: member.first_name,
        last_name: member.last_name,
        dob: member.dob,
        contact_number: member.phone_number,
        is_new_pt: patientData?.patient?.is_new_pt === '0' ? '0' : '1',
        last_seen_did: patientData?.patient?.last_seen_did,
      };
    
      setData({
        patient: {
          type: 'familyMember',
          family_member_key: memberId,
          first_name: member.first_name,
          last_name: member.last_name,
          dob: member.dob,
          contact_number: member.phone_number,
          is_new_pt: patientData?.patient?.is_new_pt === '0' ? '0' : '1',
          last_seen_did: patientData?.patient?.last_seen_did,
        },
      });

      patientStore.setCurrentPatient(familyMemberData);
      
      if (!patientData?.patient?.is_new_pt && newPatientRestriction) {
        showPopUpAlert({
          severity: "warning",
          title: "This Appointment type not for new Patients",
          message: "Then please select other Appointment type.",
          onClose: () => {
            // Use runInAction for modifying observable state in async context
            runInAction(() => {
              servicebookingFlowStore.setIsModalOpen(false);
            });
          },
        });
        return;
      }
      
      // Use runInAction for modifying observable state in async context
      runInAction(() => {
        if (servicebookingFlowStore.isFromLoginStep) {
          logger.log('isFromLoginStep:', servicebookingFlowStore.isFromLoginStep);
          servicebookingFlowStore.goToStep(BookingStep.CONFIRMATION);
        } else  {
          logger.log('servicebookingFlowStore.previousStep:', servicebookingFlowStore.previousStep);

          servicebookingFlowStore.goToStep(BookingStep.TIME_SELECTION);
        }
      });

    } catch (error) {
      logError('Error fetching patient data', error);
      logger.log('Error fetching patient data:', error);
    }
  }, [onSelectFamilyMember, getPatient, setData, showPopUpAlert, appointmentType]);

  const formattedData = useMemo(() => {
    const familyMembers =
      !!userData &&
      Object.keys(userData?.user?.family_members || {}).map(
        (memberId) => {
          const member = userData?.user?.family_members?.[memberId];
          return (
            <Button
              key={memberId}
              onClick={() => handleSelect(memberId, member)}
              mb={3}
              width="100%"
              boxShadow="md"
              border="2px"
              borderColor={isServiceBooking ? configurationDetails?.colorScheme?.buttonBg : "#B92F2F"}
              variant='outline'
            >
              {`${member?.first_name} ${member?.last_name}`} 
            </Button>
          );
        }
      );

    if (!familyMembers || familyMembers.length === 0) {
      return null;
    }

    return familyMembers;
  }, [userData, isServiceBooking, handleSelect, configurationDetails]);

  const renderFamilyMembers = useMemo(() => {
    if (userLoading) {
      return <Loading />;
    }

    if (userError) {
      return <Text id="myFamilyScreen.error">Family members loading error</Text>;
    }

    if (!formattedData) {
      return <Text id="myFamilyScreen.noResults">No Family Member added to your profile</Text>;
    }

    return formattedData;
  }, [userLoading, userError, formattedData]);

  return (
    <Box>
      {!isCreateFamilyMemberOpen ? (
        <>
          <VStack>
            {renderFamilyMembers}
          </VStack>
          <Button
            bg={isServiceBooking ? configurationDetails?.colorScheme?.buttonBg : "#B92F2F"}
            color="#fff"
            mt={5}
            width="100%"
            mb="20px"
            onClick={() => setIsCreateFamilyMemberOpen(true)}
          >
            Add new Family Member
          </Button>
        </>
      ) : (
        <CreateFamilyMemberStep isOpen={isCreateFamilyMemberOpen} onClose={() => setIsCreateFamilyMemberOpen(false)} />
      )}
    </Box>
  )
});

export default FamilyMemberStep;